import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, Trash2, Save, X, Edit3, Briefcase, Palette, Cog, Truck, HeartHandshake, Send, Users, ShieldCheck, Sparkles } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";

export const ICONS = {
  briefcase: Briefcase,
  palette: Palette,
  cog: Cog,
  truck: Truck,
  handshake: HeartHandshake,
  send: Send,
  users: Users,
  shield: ShieldCheck,
  sparkles: Sparkles,
};

const ICON_KEYS = Object.keys(ICONS);
const empty = () => ({
  title: "", employment_type: "Full-time · On-site", icon: "briefcase",
  description: "", skills: [], apply_email: "print@brothersdigital.in",
  display_order: 0, active: true,
});

function Editor({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || empty());
  const [skillInput, setSkillInput] = useState((initial?.skills || []).join(", "));
  const set = (k, v) => setF({ ...f, [k]: v });

  const handleSave = () => onSave({
    ...f,
    skills: skillInput.split(",").map((s) => s.trim()).filter(Boolean),
  });

  const IconPreview = ICONS[f.icon] || Briefcase;

  return (
    <div className="card-swiss p-5 space-y-3" data-testid="vacancy-editor">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <input placeholder="Job title *" value={f.title} onChange={(e) => set("title", e.target.value)}
          className="md:col-span-3 border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" data-testid="v-title-input" />
        <input type="number" placeholder="Order" value={f.display_order}
          onChange={(e) => set("display_order", Number(e.target.value))}
          className="border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="v-order-input" />
        <input placeholder="Employment type" value={f.employment_type}
          onChange={(e) => set("employment_type", e.target.value)}
          className="md:col-span-2 border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="v-type-input" />
        <input placeholder="Apply email" value={f.apply_email}
          onChange={(e) => set("apply_email", e.target.value)}
          className="md:col-span-2 border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="v-email-input" />
        <div className="md:col-span-2 flex items-center gap-2">
          <div className="w-11 h-11 rounded-lg bg-[#FF6B00]/10 flex items-center justify-center flex-shrink-0">
            <IconPreview size={20} className="text-[#FF6B00]" />
          </div>
          <select value={f.icon} onChange={(e) => set("icon", e.target.value)}
            className="flex-1 border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="v-icon-select">
            {ICON_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="v-active-toggle" />
          Show on website
        </label>
      </div>
      <textarea rows={3} placeholder="Job description" value={f.description}
        onChange={(e) => set("description", e.target.value)}
        className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" data-testid="v-desc-input" />
      <input placeholder="Skills (comma-separated)" value={skillInput}
        onChange={(e) => setSkillInput(e.target.value)}
        className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="v-skills-input" />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-swiss btn-outline"><X size={14} /> Cancel</button>
        <button onClick={handleSave} disabled={saving || !f.title || !f.description}
          className="btn-swiss btn-accent" data-testid="v-save-btn">
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function Vacancies() {
  usePageTitle("Career Vacancies");
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/careers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    setErr("");
    setSaving(true);
    try {
      if (editing && editing.id) await api.put(`/careers/${editing.id}`, payload);
      else await api.post("/careers", payload);
      setEditing(null); setCreating(false);
      load();
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this vacancy?")) return;
    await api.delete(`/careers/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="vacancies-root">
      <div className="border-b border-[hsl(var(--border))] pb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Content</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Career Vacancies</h1>
          <p className="text-sm text-muted-foreground mt-2">Openings shown on the public <span className="font-mono text-[#FF6B00]">/careers</span> page. Toggle to hide without deleting.</p>
        </div>
        {!creating && !editing && (
          <button onClick={() => setCreating(true)} className="btn-swiss btn-accent" data-testid="new-vacancy-btn">
            <Plus size={14} /> Add Vacancy
          </button>
        )}
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      {creating && <Editor initial={empty()} saving={saving} onCancel={() => setCreating(false)} onSave={save} />}

      <div className="space-y-3">
        {rows.map((r) => {
          if (editing?.id === r.id) return <Editor key={r.id} initial={r} saving={saving} onCancel={() => setEditing(null)} onSave={save} />;
          const Icon = ICONS[r.icon] || Briefcase;
          return (
            <div key={r.id} className="card-swiss p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-testid={`v-row-${r.id}`}>
              <div className="md:col-span-1 flex md:justify-center">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center">
                  <Icon size={22} className="text-[#FF6B00]" />
                </div>
              </div>
              <div className="md:col-span-8">
                <div className="font-display text-xl font-black">{r.title}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{r.employment_type}</div>
                <p className="mt-2 text-sm">{r.description}</p>
                {(r.skills || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {r.skills.map((s) => (
                      <span key={s} className="text-[11px] font-semibold border border-[hsl(var(--border))] rounded-full px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-2">Order: <span className="font-mono">{r.display_order}</span> · Apply: <span className="font-mono">{r.apply_email}</span></div>
              </div>
              <div className="md:col-span-3 flex flex-col md:items-end gap-2">
                <span className={`inline-flex items-center gap-2 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${r.active ? "border-emerald-500 text-emerald-600" : "border-zinc-400 text-zinc-500"}`}>
                  {r.active ? "Live" : "Hidden"}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(r)} className="btn-swiss btn-outline text-xs" data-testid={`edit-v-${r.id}`}>
                    <Edit3 size={12} /> Edit
                  </button>
                  <button onClick={() => remove(r.id)} className="btn-swiss btn-outline text-xs text-red-500 border-red-500" data-testid={`delete-v-${r.id}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && !creating && (
          <div className="card-swiss p-8 text-center text-muted-foreground text-sm">
            No vacancies yet. Add your first one to show it on <span className="font-mono">/careers</span>.
          </div>
        )}
      </div>
    </div>
  );
}
